// =====================================================================
// i18n (t_3ce8079f, registry-driven per SP-295 leg D4 / t_keyvault-i18n-d4):
// fleet-rule translations for keyvault.
//
// Keyvault is a plain Vite SPA (no SvelteKit), so the fleet's
// i18nStore (which depends on $app/environment + Mother registration)
// cannot run here. This module implements the fleet-CANONICAL API shape
// instead: object-form t(key, { en, nb }, params?) with English fallback
// and {named} interpolation. Locale is client-persisted; default en.
// Only the observability surfaces added in t_3ce8079f are wired to it —
// retrofitting the rest of the SPA is out of scope for this card.
//
// SP-295D4 change: the activated-language set is no longer the closed
// literal ['en','nb'] in LOGIC. It hydrates from the keyvault backend's
// registry-backed GET /api/languages (Mother proxy; fail-soft seed
// ['en','nb'] per SP-293 doctrine — the seed lives ONLY there and in the
// backend route, never as a logic constant here). Locale is typed as an
// open string (the `Language` alias is kept for the fleet family diff);
// $t resolution stays current-locale → en → first available → key.
// =====================================================================

/**
 * Open locale type (SP-295D4): any activated registry code, not a closed
 * union. Inline message maps stay SEED-LEGAL inline {en, nb} at callsites.
 */
export type Language = string;
/** Kept as an alias of Language for fleet family compatibility. */
export type Locale = Language;

/** Fail-soft seed — mirrors the backend's /api/languages fallback shape. */
const SEED_LOCALES: Language[] = ['en', 'nb'];

/** Inline message map — the fleet-canonical object form. */
export type InlineMessages = { en: string; nb: string } & Partial<Record<string, string>>;

const STORAGE_KEY = 'keyvault.locale';

function isValidLocale(candidate: string | null, activated: Language[]): candidate is Language {
    return candidate !== null && activated.includes(candidate);
}

function initialLocale(activated: Language[]): Language {
    if (typeof localStorage === 'undefined') return 'en';
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (isValidLocale(stored, activated)) return stored;
    } catch {
        // storage unavailable (private mode etc.) — default en
    }
    return 'en';
}

class I18nStore {
    locale = $state<Language>('en');
    /** Activated-for-users locales, hydrated from the backend registry proxy. */
    supportedLocales = $state<Language[]>(SEED_LOCALES);
    /** True once /api/languages answered (seeded or registry-backed). */
    hydrated = $state(false);

    /** Fail-soft hydration: never throws, never zero-activates. */
    async hydrate(): Promise<void> {
        if (this.hydrated) return;
        try {
            const response = await fetch('/api/languages');
            if (response.ok) {
                const data = (await response.json()) as { activatedForUsers?: Language[] };
                const list = Array.isArray(data.activatedForUsers) && data.activatedForUsers.length > 0
                    ? data.activatedForUsers
                    : SEED_LOCALES;
                this.supportedLocales = list;
            }
        } catch {
            // Mother/backend unreachable — keep fail-soft seed.
        } finally {
            this.hydrated = true;
            // Re-validate any persisted locale against the activated set.
            this.locale = initialLocale(this.supportedLocales);
        }
    }

    /** Cycle through activated locales: en → nb → ... → en (SP-295D4). */
    cycleLocale(): void {
        const list = this.supportedLocales.length > 0 ? this.supportedLocales : SEED_LOCALES;
        const idx = list.indexOf(this.locale);
        this.setLocale(list[(idx + 1) % list.length]);
    }

    setLocale(locale: Language): void {
        this.locale = locale;
        try {
            localStorage.setItem(STORAGE_KEY, locale);
        } catch {
            // non-fatal: locale just won't persist
        }
    }

    /**
     * Object-form translation: t('some.key', { en: '...', nb: '...' }, params?).
     * Resolution: current locale → English → first available message → key.
     * {name} placeholders are interpolated after selection; unknown params
     * resolve to ''.
     */
    t(
        key: string,
        messages: InlineMessages,
        params?: Record<string, string | number>
    ): string {
        const raw =
            messages[this.locale] ??
            messages.en ??
            Object.values(messages).find((v) => typeof v === 'string') ??
            key;
        if (!params) return raw;
        return raw.replace(/\{(\w+)\}/g, (_, k: string) => String(params[k] ?? ''));
    }
}

export const i18n = new I18nStore();

/** Bound so `t` stays reactive in templates after re-render of `i18n.locale`. */
export const t = i18n.t.bind(i18n);
